import { CBINSIGHTS_AVATAR, CBINSIGHTS_FORMATTED_NAME, PITCHBOOK_AVATAR, PITCHBOOK_FORMATTED_NAME, STATISTA_AVATAR, STATISTA_FORMATTED_NAME, WILEY_AVATAR, WILEY_FORMATTED_NAME, } from '#pplx/frontend/branded-sources/consts.js';
import { getFileIconForExtension } from '#pplx/frontend/components/links/fileTypeVisuals.js';
import { ASANA_AVATAR, ASANA_FORMATTED_NAME, ATLASSIAN_AVATAR, ATLASSIAN_FORMATTED_NAME, BOX_AVATAR, BOX_FORMATTED_NAME, CONFLUENCE_AVATAR, CONFLUENCE_FORMATTED_NAME, CRUNCHBASE_AVATAR, CRUNCHBASE_FORMATTED_NAME, DROPBOX_AVATAR, DROPBOX_FORMATTED_NAME, FACTSET_AVATAR, FACTSET_FORMATTED_NAME, FINANCE_AVATAR, GITHUB_AVATAR, GITHUB_FORMATTED_NAME, GMAIL_AVATAR, GMAIL_FORMATTED_NAME, GOOGLE_CALENDAR_AVATAR, GOOGLE_CALENDAR_FORMATTED_NAME, GOOGLE_DRIVE_AVATAR, GOOGLE_DRIVE_FORMATTED_NAME, JIRA_AVATAR, JIRA_FORMATTED_NAME, LINEAR_AVATAR, LINEAR_FORMATTED_NAME, MAX_FILE_UPLOAD, MICROSOFT_TEAMS_AVATAR, MICROSOFT_TEAMS_FORMATTED_NAME, NOTION_AVATAR, NOTION_FORMATTED_NAME, ONEDRIVE_AVATAR, ONEDRIVE_FORMATTED_NAME, OUTLOOK_AVATAR, OUTLOOK_FORMATTED_NAME, PLAID_FORMATTED_NAME, SHAREPOINT_AVATAR, SHAREPOINT_FORMATTED_NAME, SLACK_AVATAR, SLACK_DIRECT_FORMATTED_NAME, SLACK_FORMATTED_NAME, ZOOM_AVATAR, ZOOM_FORMATTED_NAME, MEDICAL_RECORDS_AVATAR, MEDICAL_RECORDS_FORMATTED_NAME, FUNCTION_HEALTH_AVATAR, FUNCTION_HEALTH_FORMATTED_NAME, } from '#pplx/frontend/components/utils/consts.js';
export const getIconForExtension = (extension) => getFileIconForExtension(extension);
export function formatFileSize(bytes, intl) {
    const sizes = ['bytes', 'KB', 'MB', 'GB', 'TB'];
    const unitIndex = bytes === 0 ? 0 : Math.floor(Math.log(bytes) / Math.log(1024));
    const value = Math.ceil(bytes / 1024 ** unitIndex);
    if (!intl)
        return `${value} ${sizes[unitIndex]}`;
    const units = ['byte', 'kilobyte', 'megabyte', 'gigabyte', 'terabyte'];
    return intl.formatNumber(value, {
        style: 'unit',
        unit: units[unitIndex],
        unitDisplay: 'short',
        maximumFractionDigits: 0,
        useGrouping: false,
    });
}
export const formatFileMetadata = (args) => {
    return [
        args.extension?.toUpperCase(),
        args.fileSize > 0 ? formatFileSize(args.fileSize, args.intl) : null,
    ]
        .filter(Boolean)
        .join(' · ');
};
/**
 * Extracts the file extension from a filename.
 * Returns the extension in lowercase without the leading dot, or undefined if no extension.
 *
 * @param filename - The filename to extract extension from
 * @returns The lowercase extension without the dot, or undefined
 *
 * @example
 * getFileExtension('document.pdf') // 'pdf'
 * getFileExtension('archive.tar.gz') // 'gz'
 * getFileExtension('README') // undefined
 */
export const getFileExtension = (filename) => {
    if (!filename)
        return undefined;
    const lastDotIndex = filename.lastIndexOf('.');
    // No dot, or dot is at the start (dotfile), or dot is at the end
    if (lastDotIndex <= 0 || lastDotIndex === filename.length - 1) {
        return undefined;
    }
    return filename.substring(lastDotIndex + 1).toLowerCase();
};
const DEFAULT_CHARACTERS_BEFORE_EXTENSION = 5;
export function splitFileNameForDisplay(fileName, charactersBeforeExtension = DEFAULT_CHARACTERS_BEFORE_EXTENSION) {
    const segmenter = typeof Intl.Segmenter === 'function'
        ? new Intl.Segmenter(undefined, { granularity: 'grapheme' })
        : undefined;
    const segmentFileNamePart = (value) => segmenter ? Array.from(segmenter.segment(value), (part) => part.segment) : Array.from(value);
    const fileNameSegments = segmentFileNamePart(fileName);
    const extension = getFileExtension(fileName);
    const extensionLength = extension
        ? segmentFileNamePart(fileName.slice(fileName.lastIndexOf('.'))).length
        : 0;
    const splitIndex = Math.max(0, fileNameSegments.length - Math.max(0, charactersBeforeExtension) - extensionLength);
    return {
        start: fileNameSegments.slice(0, splitIndex).join(''),
        end: fileNameSegments.slice(splitIndex).join(''),
    };
}
/**
 * Maximum filename length to ensure compatibility across filesystems.
 * Most filesystems support 255 bytes, but we use 200 to be conservative
 * and account for extensions and multi-byte UTF-8 characters.
 */
const MAX_FILENAME_LENGTH = 200;
/**
 * Sanitizes a string to be safe for use as a filename across different filesystems.
 * Removes or replaces characters that are invalid in Windows, macOS, and Linux filenames.
 * Handles filenames with or without extensions intelligently.
 *
 * Invalid characters:
 * - `/` and `\` - Directory separators
 * - `:` - Reserved on Windows (drive letters)
 * - `*`, `?` - Wildcards
 * - `"`, `<`, `>`, `|` - Shell/command-line special characters
 *
 * @param filename - The filename to sanitize (with or without extension)
 * @param options - Optional configuration
 * @param options.maxLength - Maximum length for the full sanitized filename (default: MAX_FILENAME_LENGTH)
 * @param options.replacement - Character to replace invalid characters with (default: ' ' space for readability)
 * @param options.fallback - Fallback filename if sanitization results in empty string (default: 'file')
 * @returns Sanitized filename safe for filesystem use
 *
 * @example
 * // With extension - extension is preserved
 * sanitizeFilename('Report: Q1/Q2.pdf') // 'Report  Q1 Q2.pdf'
 * sanitizeFilename('File<Name>*.xlsx', { replacement: '_' }) // 'File_Name__.xlsx'
 *
 * @example
 * // Without extension
 * sanitizeFilename('Report: Q1/Q2 Sales') // 'Report  Q1 Q2 Sales'
 * sanitizeFilename('System Architecture <v2>', { replacement: '-' }) // 'System Architecture -v2-'
 *
 * @example
 * // Truncation preserves extension
 * sanitizeFilename('Very Long Name That Exceeds Limit.pdf', { maxLength: 20 }) // 'Very Long Name T.pdf'
 *
 * @example
 * // Edge cases
 * sanitizeFilename('', { fallback: 'untitled' }) // 'untitled'
 * sanitizeFilename('///', { fallback: 'document' }) // 'document'
 */
export const sanitizeFilename = (filename, options = {}) => {
    const { maxLength = MAX_FILENAME_LENGTH, replacement = ' ', fallback = 'file' } = options;
    if (!filename)
        return fallback;
    // Split filename into name and extension
    const lastDotIndex = filename.lastIndexOf('.');
    const hasExtension = lastDotIndex > 0 && lastDotIndex < filename.length - 1;
    let name = hasExtension ? filename.substring(0, lastDotIndex) : filename;
    const extension = hasExtension ? filename.substring(lastDotIndex) : '';
    // Replace invalid filesystem characters in the name part
    name = name.replace(/[/\\:*?"<>|]/g, replacement).trim();
    // Fallback if name is empty after sanitization
    if (!name) {
        name = fallback;
    }
    // Truncate if too long, accounting for extension
    if (name.length + extension.length > maxLength) {
        const maxNameLength = maxLength - extension.length;
        if (maxNameLength > 0) {
            name = name.substring(0, maxNameLength).trim();
        }
        else {
            // Extension alone is too long, fallback
            return fallback + extension;
        }
    }
    return name + extension;
};
export const isParentFileType = (file) => {
    const fileType = file.file_metadata.file_type;
    return (fileType === 'FOLDER' ||
        fileType === 'DATABASE' ||
        fileType === 'WORKSPACE' ||
        fileType === 'PAGE');
};
export const CONNECTOR_IDS = {
    crunchbase: 'crunchbase',
    factset: 'factset',
    wiley: 'wiley', // deprecated
    google_drive: 'google_drive',
    onedrive: 'onedrive',
    sharepoint: 'sharepoint',
    gcal: 'gcal',
    dropbox: 'dropbox',
    box: 'box',
    notion_mcp: 'notion_mcp',
    outlook: 'outlook',
    linear: 'linear',
    linear_alt: 'linear_alt',
    slack: 'slack',
    slack_direct: 'slack_direct',
    github_mcp_direct: 'github_mcp_direct',
    asana_mcp_direct: 'asana_mcp_direct',
    asana_mcp_merge: 'asana_mcp_merge',
    atlassian_mcp_direct: 'atlassian_mcp_direct',
    jira_mcp_merge: 'jira_mcp_merge',
    confluence_mcp_merge: 'confluence_mcp_merge',
    microsoft_teams_mcp_merge: 'microsoft_teams_mcp_merge',
    wiley_mcp_cashmere: 'wiley_mcp_cashmere',
    cbinsights_mcp_cashmere: 'cbinsights_mcp_cashmere',
    pitchbook_mcp_cashmere: 'pitchbook_mcp_cashmere',
    statista_mcp_cashmere: 'statista_mcp_cashmere',
    zoom: 'zoom',
    edgar: 'edgar', // deprecated
    apple_healthkit: 'apple_healthkit',
    medical_records: 'medical_records',
    wearables: 'wearables',
    plaid: 'plaid',
    function_health: 'function_health',
    openrouter: 'openrouter',
};
export const isConnectorId = (value) => {
    const isConnectorId = Object.keys(CONNECTOR_IDS).includes(value);
    // FIXME: gmail isn't a valid connector id, but it's used in the FE somewhere
    return isConnectorId || value === 'gmail';
};
export const getConnectorAvatar = (connectorName) => {
    switch (connectorName) {
        case 'crunchbase':
            return CRUNCHBASE_AVATAR;
        case 'factset':
            return FACTSET_AVATAR;
        case 'wiley':
            return WILEY_AVATAR;
        case 'google_drive':
            return GOOGLE_DRIVE_AVATAR;
        case 'onedrive':
            return ONEDRIVE_AVATAR;
        case 'sharepoint':
            return SHAREPOINT_AVATAR;
        case 'gcal':
            return GOOGLE_CALENDAR_AVATAR;
        case 'dropbox':
            return DROPBOX_AVATAR;
        case 'box':
            return BOX_AVATAR;
        // @ts-expect-error - FIXME: Missing from BE API
        case 'gmail':
            return GMAIL_AVATAR;
        case 'notion_mcp':
            return NOTION_AVATAR;
        case 'outlook':
            return OUTLOOK_AVATAR;
        // @ts-expect-error - FIXME: Missing from BE API
        case 'linear_native':
        case 'linear':
        case 'linear_alt':
            return LINEAR_AVATAR;
        case 'slack':
        case 'slack_direct':
            return SLACK_AVATAR;
        case 'github_mcp_direct':
            return GITHUB_AVATAR;
        case 'asana_mcp_direct':
        case 'asana_mcp_merge':
            return ASANA_AVATAR;
        case 'atlassian_mcp_direct':
            return ATLASSIAN_AVATAR;
        case 'jira_mcp_merge':
            return JIRA_AVATAR;
        case 'confluence_mcp_merge':
            return CONFLUENCE_AVATAR;
        case 'microsoft_teams_mcp_merge':
            return MICROSOFT_TEAMS_AVATAR;
        case 'wiley_mcp_cashmere':
            return WILEY_AVATAR;
        case 'cbinsights_mcp_cashmere':
            return CBINSIGHTS_AVATAR;
        case 'pitchbook_mcp_cashmere':
            return PITCHBOOK_AVATAR;
        case 'statista_mcp_cashmere':
            return STATISTA_AVATAR;
        case 'zoom':
            return ZOOM_AVATAR;
        case 'medical_records':
            return MEDICAL_RECORDS_AVATAR;
        case 'function_health':
            return FUNCTION_HEALTH_AVATAR;
        // @ts-expect-error - FIXME: Missing from BE API
        case 'finance':
        case 'plaid':
            return FINANCE_AVATAR;
        default:
            return null;
    }
};
export const formatConnectorName = (connectorName) => {
    switch (connectorName) {
        case 'crunchbase':
            return CRUNCHBASE_FORMATTED_NAME;
        case 'factset':
            return FACTSET_FORMATTED_NAME;
        case 'google_drive':
            return GOOGLE_DRIVE_FORMATTED_NAME;
        case 'onedrive':
            return ONEDRIVE_FORMATTED_NAME;
        case 'sharepoint':
            return SHAREPOINT_FORMATTED_NAME;
        case 'gcal':
            return GOOGLE_CALENDAR_FORMATTED_NAME;
        case 'dropbox':
            return DROPBOX_FORMATTED_NAME;
        case 'box':
            return BOX_FORMATTED_NAME;
        case 'gmail':
            return GMAIL_FORMATTED_NAME;
        case 'notion_mcp':
            return NOTION_FORMATTED_NAME;
        case 'outlook':
            return OUTLOOK_FORMATTED_NAME;
        case 'linear':
        case 'linear_alt':
        case 'linear_native':
            return LINEAR_FORMATTED_NAME;
        case 'slack':
            return SLACK_FORMATTED_NAME;
        case 'slack_direct':
            return SLACK_DIRECT_FORMATTED_NAME;
        case 'github_mcp_direct':
            return GITHUB_FORMATTED_NAME;
        case 'asana_mcp_direct':
            return ASANA_FORMATTED_NAME;
        case 'asana_mcp_merge':
            return ASANA_FORMATTED_NAME;
        case 'atlassian_mcp_direct':
            return ATLASSIAN_FORMATTED_NAME;
        case 'jira_mcp_merge':
            return JIRA_FORMATTED_NAME;
        case 'confluence_mcp_merge':
            return CONFLUENCE_FORMATTED_NAME;
        case 'microsoft_teams_mcp_merge':
            return MICROSOFT_TEAMS_FORMATTED_NAME;
        case 'wiley_mcp_cashmere':
            return WILEY_FORMATTED_NAME;
        case 'cbinsights_mcp_cashmere':
            return CBINSIGHTS_FORMATTED_NAME;
        case 'pitchbook_mcp_cashmere':
            return PITCHBOOK_FORMATTED_NAME;
        case 'statista_mcp_cashmere':
            return STATISTA_FORMATTED_NAME;
        case 'zoom':
            return ZOOM_FORMATTED_NAME;
        case 'medical_records':
            return MEDICAL_RECORDS_FORMATTED_NAME;
        case 'function_health':
            return FUNCTION_HEALTH_FORMATTED_NAME;
        case 'health':
            return 'Health';
        case 'plaid':
            return PLAID_FORMATTED_NAME;
        default:
            return connectorName;
    }
};
const DATE_TIMEZONE_SUFFIX_REGEX = /(?:Z|[+-]\d{2}:?\d{2})$/u;
export const parseFileDate = (dateString) => {
    return new Date(DATE_TIMEZONE_SUFFIX_REGEX.test(dateString) ? dateString : `${dateString}Z`);
};
// Format in user's local timezone
export const formatDateWithTime = (dateString, locale) => {
    const date = parseFileDate(dateString);
    return date.toLocaleString(locale, {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
};
// Format in user's local timezone.
export const formatDateWithoutTime = (dateString, locale) => {
    const date = parseFileDate(dateString);
    return date.toLocaleDateString(locale, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
};
/**
 * Gets display information for file status, including error details.
 * Returns status label and additional error context for SYNC_ERROR states.
 */
export const getFileStatusInfo = (status, intl, errorCode) => {
    switch (status) {
        case 'RECEIVED':
        case 'QUEUED':
        case 'RATE_LIMITED':
            return {
                label: intl.formatMessage({
                    id: '1e6fc864a5',
                    description: 'Label in the file Utils utility section',
                    defaultMessage: 'Queued',
                }),
            };
        case 'SYNCING':
            return {
                label: intl.formatMessage({
                    id: '721dbeb104',
                    description: 'Label in the file Utils utility section',
                    defaultMessage: 'Syncing',
                }),
            };
        case 'EVALUATING_RESYNC':
        case 'READY':
            return {
                label: intl.formatMessage({
                    id: '58e9477584',
                    description: 'Label in the file Utils utility section',
                    defaultMessage: 'Ready',
                }),
            };
        case 'SYNC_ERROR':
            return getFileErrorDetails(errorCode, intl);
    }
};
/**
 * Maps FileErrorType codes to user-friendly error messages.
 * Distinguishes between user-actionable errors (file too large, wrong format, etc.)
 * and system errors (parsing failures, rate limits, etc.)
 */
const getFileErrorDetails = (errorCode, intl) => {
    if (!errorCode) {
        return {
            label: intl.formatMessage({
                id: '5ebdcc596c',
                description: 'Error message in the file Utils utility section',
                defaultMessage: 'Sync error',
            }),
            description: intl.formatMessage({
                defaultMessage: 'An unknown error occurred while syncing this file',
                description: 'Error message in the fileUtils utility section',
                id: 'ff225f76c4',
            }),
        };
    }
    switch (errorCode) {
        case 'INVALID_FILE_SIZE':
            return {
                label: intl.formatMessage({
                    id: '156b22efb6',
                    description: 'Text in the file Utils utility section',
                    defaultMessage: 'File too large',
                }),
                description: intl.formatMessage({
                    defaultMessage: 'File size exceeds the maximum allowed limit',
                    description: 'Error message in the fileUtils utility section',
                    id: 'b34ac6e8bb',
                }),
                suggestion: intl.formatMessage({
                    defaultMessage: 'Try uploading a smaller file or contact support to increase your limit',
                    description: 'Suggestion message in the fileUtils utility section',
                    id: 'ff3b98a0dd',
                }),
            };
        case 'INVALID_FILE_TYPE':
            return {
                label: intl.formatMessage({
                    id: '583661fe92',
                    description: 'Label in the fileUtils utility section',
                    defaultMessage: 'Not supported',
                }),
                description: intl.formatMessage({
                    defaultMessage: 'File type is not supported',
                    description: 'Error message in the fileUtils utility section',
                    id: '89f4e0577d',
                }),
                suggestion: intl.formatMessage({
                    defaultMessage: 'Check the list of supported file formats and convert your file if needed',
                    description: 'Suggestion message in the fileUtils utility section',
                    id: '45820bb4c3',
                }),
            };
        case 'BAD_FILE':
            return {
                label: intl.formatMessage({
                    id: '583661fe92',
                    description: 'Label in the fileUtils utility section',
                    defaultMessage: 'Not supported',
                }),
                description: intl.formatMessage({
                    defaultMessage: 'File is corrupted or cannot be read',
                    description: 'Error message in the fileUtils utility section',
                    id: 'b66fba8481',
                }),
                suggestion: intl.formatMessage({
                    defaultMessage: 'Try re-uploading the file or check if it opens correctly on your device',
                    description: 'Suggestion message in the fileUtils utility section',
                    id: 'ab5fcaa50b',
                }),
            };
        case 'TOKEN_LIMIT_EXCEEDED':
            return {
                label: intl.formatMessage({
                    id: 'd185abac14',
                    description: 'Label in the fileUtils utility section',
                    defaultMessage: 'Content too long',
                }),
                description: intl.formatMessage({
                    defaultMessage: 'File content exceeds the token limit for processing',
                    description: 'Error message in the fileUtils utility section',
                    id: 'ddae880717',
                }),
                suggestion: intl.formatMessage({
                    defaultMessage: 'Try splitting the file into smaller parts or contact support',
                    description: 'Suggestion message in the fileUtils utility section',
                    id: '7a908e4e43',
                }),
            };
        case 'AUDIO_EMPTY_CONTENT':
            return {
                label: intl.formatMessage({
                    id: '43c4faf4fc',
                    defaultMessage: 'The attached audio file contains no detectable speech.',
                    description: 'Attachment error when audio transcription returns no text',
                }),
            };
        case 'AUDIO_DURATION_LIMIT':
            return {
                label: intl.formatMessage({
                    defaultMessage: 'Audio exceeds the 60-minute limit.',
                    id: 'af265dd558',
                    description: 'Attachment error when audio is longer than the transcription limit',
                }),
            };
        case 'FAILED_MODERATION':
            return {
                label: intl.formatMessage({
                    id: '8975efa112',
                    description: 'Label in the fileUtils utility section',
                    defaultMessage: 'Content policy violation',
                }),
                description: intl.formatMessage({
                    defaultMessage: 'File content does not meet our content policy',
                    description: 'Error message in the fileUtils utility section',
                    id: 'b9b0426789',
                }),
                suggestion: intl.formatMessage({
                    defaultMessage: 'Review the file content and try again with appropriate content',
                    description: 'Suggestion message in the fileUtils utility section',
                    id: '51b60c5143',
                }),
            };
        case 'FAILED_SECURITY_CHECK':
            return {
                label: intl.formatMessage({
                    id: '8975efa112',
                    description: 'Label in the fileUtils utility section',
                    defaultMessage: 'Content policy violation',
                }),
                description: intl.formatMessage({
                    defaultMessage: 'File failed security validation',
                    description: 'Error message in the fileUtils utility section',
                    id: '6bfbe6b577',
                }),
                suggestion: intl.formatMessage({
                    defaultMessage: 'The file may contain unsafe content. Try uploading a different file.',
                    description: 'Suggestion message in the fileUtils utility section',
                    id: '140e51d473',
                }),
            };
        case 'PERMISSION_DENIED':
            return {
                label: intl.formatMessage({
                    id: '3c7d12b565',
                    description: 'Label in the fileUtils utility section',
                    defaultMessage: 'Permission denied',
                }),
                description: intl.formatMessage({
                    defaultMessage: "You don't have permission to access this file",
                    description: 'Error message in the fileUtils utility section',
                    id: 'c3a4f2f8a3',
                }),
                suggestion: intl.formatMessage({
                    defaultMessage: 'Ask the file owner to grant you access, or try reconnecting your account',
                    description: 'Suggestion message in the fileUtils utility section',
                    id: '98aafeceb0',
                }),
            };
        case 'FILE_NOT_FOUND':
            return {
                label: intl.formatMessage({
                    id: '5f98537791',
                    description: 'Label in the fileUtils utility section',
                    defaultMessage: 'File not found',
                }),
                description: intl.formatMessage({
                    defaultMessage: 'This file may have been deleted or moved',
                    description: 'Error message in the fileUtils utility section',
                    id: '94af0a4ceb',
                }),
                suggestion: intl.formatMessage({
                    defaultMessage: 'Check if the file still exists in your connected account',
                    description: 'Suggestion message in the fileUtils utility section',
                    id: 'd40db827af',
                }),
            };
        default:
            return {
                label: intl.formatMessage({
                    id: '7d811cb478',
                    description: 'Label in the fileUtils utility section',
                    defaultMessage: 'Sync error',
                }),
                description: intl.formatMessage({
                    defaultMessage: 'An error occurred while syncing this file',
                    description: 'Error message in the fileUtils utility section',
                    id: 'cc6686e504',
                }),
            };
    }
};
const PREVIEW_EXTENSION_MAP = {
    pdf: 'pdf',
    docx: 'docx',
    pptx: 'pptx',
    xlsx: 'xlsx',
    png: 'image',
    jpg: 'image',
    jpeg: 'image',
    gif: 'image',
    webp: 'image',
    svg: 'image',
    md: 'text',
    markdown: 'text',
    txt: 'text',
    csv: 'text',
    tsv: 'text',
    log: 'text',
    py: 'text',
    js: 'text',
    ts: 'text',
    tsx: 'text',
    jsx: 'text',
    json: 'text',
    html: 'text',
    htm: 'text',
    css: 'text',
    yaml: 'text',
    yml: 'text',
    toml: 'text',
    sh: 'text',
    sql: 'text',
    go: 'text',
    rs: 'text',
    rb: 'text',
    java: 'text',
    kt: 'text',
    kts: 'text',
    swift: 'text',
    cpp: 'text',
    cc: 'text',
    cxx: 'text',
    hpp: 'text',
    c: 'text',
    h: 'text',
    cs: 'text',
    scala: 'text',
    hs: 'text',
    lhs: 'text',
    php: 'text',
    r: 'text',
    dart: 'text',
    ex: 'text',
    exs: 'text',
    elm: 'text',
    erl: 'text',
    hrl: 'text',
    jl: 'text',
    ml: 'text',
    mli: 'text',
    clj: 'text',
    cljs: 'text',
    cljc: 'text',
    groovy: 'text',
    gvy: 'text',
    graphql: 'text',
    gql: 'text',
    lisp: 'text',
    cl: 'text',
    el: 'text',
    fs: 'text',
    fsi: 'text',
    fsx: 'text',
    mm: 'text',
};
export function getFilePreviewType(filename) {
    const ext = getFileExtension(filename);
    return ext ? (PREVIEW_EXTENSION_MAP[ext] ?? null) : null;
}
/**
 * Whether the side file viewer renders this extension natively. Other types keep its iframe,
 * whose rendering differs from the shared preview dispatcher's for several of them.
 */
export function isNativeSideViewerExtension(extension) {
    const previewType = extension ? PREVIEW_EXTENSION_MAP[extension] : undefined;
    return previewType === 'docx' || previewType === 'xlsx';
}
export function canSideViewerRenderLocalFile(name, mimeType) {
    return mimeType === 'application/pdf' || isNativeSideViewerExtension(getFileExtension(name));
}
export const isFileSystemFileEntry = (entry) => {
    return entry.isFile;
};
export const isFileSystemDirectoryEntry = (entry) => {
    return entry.isDirectory;
};
/**
 * Recursively reads files from a browser directory entry across every reader batch.
 * Returns at most `maxFiles + 1` files so callers can reject an oversized directory atomically.
 */
export const readFilesFromDirectory = async (rootEntry, maxFiles = MAX_FILE_UPLOAD) => {
    const stack = [{ kind: 'entry', entry: rootEntry }];
    const files = [];
    while (stack.length > 0) {
        const task = stack.pop();
        if (!task) {
            continue;
        }
        if (task.kind === 'reader') {
            const entries = await new Promise((resolve) => task.reader.readEntries(resolve));
            if (entries.length > 0) {
                // Resume the reader only after processing this batch so the file limit also bounds
                // how many browser directory batches we request.
                stack.push(task, ...entries.map((entry) => ({ kind: 'entry', entry })));
            }
            continue;
        }
        const { entry } = task;
        if (isFileSystemFileEntry(entry)) {
            const file = await new Promise((resolve) => {
                entry.file(resolve);
            });
            files.push(file);
            // Purposely using > instead of >= so that a list of length maxFiles + 1
            // of files is returned that later triggers the max files error state.
            // This is to achieve an all or nothing effect where all the files in
            // a directory are uploaded or none.
            if (files.length > maxFiles) {
                return files;
            }
        }
        else if (isFileSystemDirectoryEntry(entry)) {
            stack.push({ kind: 'reader', reader: entry.createReader() });
        }
    }
    return files;
};
//# sourceMappingURL=fileUtils.js.map